import { useState, useEffect, useRef, useCallback } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { LuLoaderCircle } from "react-icons/lu";
import {
  FiCopy,
  FiTrash,
  FiImage,
  FiDownload,
  FiX,
  FiCamera,
  FiScissors,
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiUser,
  FiLink,
  FiBox,
  FiList,
} from "react-icons/fi";
import {
  MdDriveFileMove,
  MdRestoreFromTrash,
  MdDeleteForever,
} from "react-icons/md";
import { isAssetsListMediaTypeAllowed } from "@kagami/shared/assetsList";

import AssetMedia from "./AssetMedia";
import Button from "./ui/Button";
import AsyncEditableTextArea from "./ui/AsyncEditableTextArea";
import { Progress } from "./ui/shadcn/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/shadcn/tabs";
import { useShow } from "@/contexts/ShowContext";
import { UserRole } from "@/contexts/UserContext";
import { useModal } from "@/contexts/ModalContext";
import { apiFetch } from "@/lib/api";
import { isTrimSupported } from "@/lib/trimVideo";
import useFileUpload from "@/hooks/useFileUpload";
import useFileDownload from "@/hooks/useFileDownload";
import CommentGroup from "./comments/CommentGroup";
import CommentMentionsInput from "./comments/CommentMentionsInput";
import FolderPickerModal from "./modals/FolderPickerModal";
import TaskModal from "./modals/TaskModal";
import TagsModal from "./modals/TagsModal";
import CharacterAssetsModal from "./modals/CharacterAssetsModal";

// Format byte size into a human-readable string. Returns null if size is missing.
const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${m}:${String(sec).padStart(2, "0")}`;
};

function InfoRow({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-secondary-foreground/60">
        {label}
      </span>
      <div className="text-sm text-foreground break-words">{children}</div>
    </div>
  );
}

// Settings keys that are media references (inputs/outputs) rather than tunable
// generation parameters — hidden from the settings chip list.
const GENERATION_HIDDEN_KEYS = [
  "prompt",
  "asset",
  "assets",
  "video",
  "audio",
  "image",
  "image_url",
];

// Shows the generation that produced an asset (model, prompt, settings). The
// asset already carries a `generation` sub-document with the payload settings;
// we additionally fetch the underlying task async to resolve the model name and
// enable opening full task details. The section renders immediately from the
// embedded generation data and enriches once the task loads.
function GenerationInfo({ asset }) {
  const gen = asset?.generation;
  const taskId = gen?.task;
  const { openModal } = useModal();
  const [task, setTask] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTask(null);
    if (!taskId) return;
    const fetchTask = async () => {
      try {
        const res = await apiFetch(`/api/tasks/${taskId}`);
        if (!res.ok) throw new Error("Failed to fetch task");
        const data = await res.json();
        if (!cancelled) setTask(data);
      } catch (error) {
        console.error("Error fetching generation task:", error);
      }
    };
    fetchTask();
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  if (!gen) return null;

  // The task's request body is the source of truth; fall back to the settings
  // snapshot stored on the asset when the task can't be loaded.
  const payload = task?.request_body?.payload || gen.settings || {};
  const prompt = gen?.prompt;
  const modelName =
    task?.model?.name ||
    (typeof gen.model === "object" ? gen.model?.name : null);
  const settingsEntries = Object.entries(payload).filter(
    ([key, value]) =>
      !GENERATION_HIDDEN_KEYS.includes(key) &&
      value !== null &&
      value !== undefined &&
      value !== "" &&
      typeof value !== "object",
  );

  return (
    <div className="flex flex-col gap-4">
      {/* <div className='flex items-center justify-between'>
                <span className='text-[11px] uppercase tracking-wide text-secondary-foreground/60'>Generation</span>
                {task && (
                    <button
                        type='button'
                        onClick={() => openModal(<TaskModal task={task} />, 'side')}
                        className='text-[11px] text-primary hover:underline cursor-pointer'
                    >
                        View task
                    </button>
                )}
            </div> */}

      {modelName && (
        <InfoRow label="Model">
          <span className="text-xs bg-secondary/60 rounded-sm px-1.5 py-0.5 text-secondary-foreground">
            {modelName}
          </span>
        </InfoRow>
      )}

      {prompt && (
        <InfoRow label="Prompt">
          <span className="block text-xs font-mono text-secondary-foreground bg-secondary/40 rounded-md p-2 whitespace-pre-wrap">
            {prompt}
          </span>
        </InfoRow>
      )}

      {settingsEntries.length > 0 && (
        <InfoRow label="Settings">
          <div className="flex flex-wrap gap-1.5 mt-1">
            {settingsEntries.map(([key, value]) => (
              <div
                key={key}
                className="flex items-center gap-1.5 bg-secondary/50 rounded-md px-2 py-1">
                <span className="text-secondary-foreground/60 text-[10px] capitalize">
                  {key.replace(/_/g, " ")}
                </span>
                <span className="text-secondary-foreground text-xs">
                  {typeof value === "boolean"
                    ? value
                      ? "Yes"
                      : "No"
                    : String(value)}
                </span>
              </div>
            ))}
          </div>
        </InfoRow>
      )}
    </div>
  );
}

function InfoTab({ asset, onRefreshAsset }) {
  const isArchived = Boolean(asset?.archived);
  const { show } = useShow();
  const { openModal } = useModal();

  if (!asset) {
    return null;
  }

  const tagNames = (asset.tags || [])
    .map((tagId) => show?.asset_categories?.find((t) => t._id === tagId)?.name)
    .filter(Boolean);

  const assetCharacters = (asset.characters || [])
    .map((character) => {
      const id =
        typeof character === "object" && character !== null
          ? character._id
          : character;
      const found = show?.characters?.find((c) => c._id === id);
      const name =
        found?.name ||
        (typeof character === "object" && character !== null
          ? character.name
          : null);
      if (!id || !name) return null;
      return {
        _id: id,
        name,
        thumbnail_url:
          found?.thumbnail_url ||
          (typeof character === "object" && character !== null
            ? character.thumbnail_url
            : null),
      };
    })
    .filter(Boolean);

  // Edit tags in the shared side-tray modal. The refresh is passed in as
  // `onSaved` (owned by an ancestor, not gated on the modal closing) so it
  // still runs if the tray — or the whole preview — is closed before the save
  // completes.
  const openTagsModal = () => {
    openModal(
      <TagsModal
        assetCategories={show?.asset_categories || []}
        assets={[asset]}
        onSaved={() => onRefreshAsset?.(asset._id)}
      />,
    );
  };

  const openCharactersModal = () => {
    openModal(
      <CharacterAssetsModal
        characters={show?.characters || []}
        assets={[asset]}
        onSaved={() => onRefreshAsset?.(asset._id)}
      />,
    );
  };

  const sizeLabel = formatBytes(asset.size ?? asset.metadata?.size);
  const durationLabel = formatDuration(asset.duration);
  const created = asset.created_at || asset.createdAt;
  const updated = asset.updatedAt;

  const dimensions = (() => {
    const w = asset.metadata?.width || asset.width;
    const h = asset.metadata?.height || asset.height;
    if (w && h) return `${w} × ${h}`;
    return null;
  })();

  return (
    <div className="flex flex-col gap-4 p-4 overflow-y-auto h-full">
      {asset.description && (
        <InfoRow label="Description">{asset.description}</InfoRow>
      )}
      {asset.aiDescription && (
        <InfoRow label="AI Description">
          <span className="text-secondary-foreground/80">
            {asset.aiDescription}
          </span>
        </InfoRow>
      )}
      <div className="grid grid-cols-2 gap-4">
        <InfoRow label="Type">{asset.mediaType || "-"}</InfoRow>
        <InfoRow label="Status">
          <span className="capitalize">{asset.status || "draft"}</span>
        </InfoRow>
        {dimensions && <InfoRow label="Dimensions">{dimensions}</InfoRow>}
        {durationLabel && <InfoRow label="Duration">{durationLabel}</InfoRow>}
        {sizeLabel && <InfoRow label="Size">{sizeLabel}</InfoRow>}
        {asset.source && <InfoRow label="Source">{asset.source}</InfoRow>}
        {created && (
          <InfoRow label="Created">
            {dayjs(created).format("MMM D, YYYY h:mm A")}
          </InfoRow>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-secondary-foreground/60">
            Tags
          </span>
          {isArchived === false && (
            <button
              type="button"
              onClick={openTagsModal}
              title="Edit tags"
              className="p-0.5 rounded-sm text-secondary-foreground/70 hover:text-secondary-foreground cursor-pointer">
              <FiEdit2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="text-sm text-foreground break-words">
          {tagNames.length > 0 ? (
            <div className="flex flex-wrap gap-1 mt-1">
              {tagNames.map((name) => (
                <span
                  key={name}
                  className="text-[11px] py-[1px] px-1.5 rounded-sm capitalize bg-primary text-primary-foreground">
                  {name}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-secondary-foreground/60">
              No tags
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-secondary-foreground/60">
            Characters
          </span>
          {isArchived === false && (
            <button
              type="button"
              onClick={openCharactersModal}
              title="Edit characters"
              className="p-0.5 rounded-sm text-secondary-foreground/70 hover:text-secondary-foreground cursor-pointer">
              <FiEdit2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="text-sm text-foreground break-words">
          {assetCharacters.length > 0 ? (
            <div className="flex flex-col gap-1.5 mt-1">
              {assetCharacters.map((character) => (
                <div
                  key={character._id}
                  className="flex items-center gap-2 min-w-0">
                  {character.thumbnail_url ? (
                    <img
                      src={character.thumbnail_url}
                      alt={character.name}
                      loading="lazy"
                      decoding="async"
                      className="w-6 h-6 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <span className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center shrink-0">
                      <FiUser className="w-3 h-3 text-secondary-foreground" />
                    </span>
                  )}
                  <span className="text-xs truncate">{character.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-xs text-secondary-foreground/60">
              No characters
            </span>
          )}
        </div>
      </div>

      <GenerationInfo asset={asset} />

      {asset.url && (
        <InfoRow label="URL">
          <a
            href={asset.url}
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline text-xs break-all">
            {asset.url}
          </a>
        </InfoRow>
      )}
    </div>
  );
}

// Asset comments tab — mirrors the TimelineComments pattern (CommentGroup +
// CommentMentionsInput + optimistic add/update) but without the timeline-only
// concerns (playhead timestamp, episode/segment context).
function AssetComments({ assetId }) {
  const { show } = useShow();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const commentInputRef = useRef(null);

  const fetchComments = useCallback(async () => {
    if (!show?._id || !assetId) {
      setComments([]);
      setLoading(false);
      return;
    }
    try {
      const res = await apiFetch(
        `/api/shows/${show._id}/comments?uid=${assetId}`,
      );
      if (!res.ok) throw new Error("Failed to fetch comments");
      const data = await res.json();
      setComments(data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  }, [show?._id, assetId]);

  useEffect(() => {
    setLoading(true);
    fetchComments();
  }, [fetchComments]);

  const addComment = useCallback((comment) => {
    setComments((prev) => [...prev, comment]);
  }, []);

  const updateComment = useCallback((updated) => {
    setComments((prev) =>
      prev.map((c) => (c._id === updated._id ? { ...c, ...updated } : c)),
    );
  }, []);

  const handleCommentSubmit = async () => {
    if (!commentText.trim() || posting) return;
    try {
      setPosting(true);
      const res = await apiFetch(`/api/shows/${show._id}/comments`, {
        method: "POST",
        body: JSON.stringify({
          uid: assetId,
          text: commentText,
          element_type: "asset",
        }),
      });
      if (!res.ok) throw new Error("Failed to submit comment");
      const data = await res.json();
      addComment(data);
      setCommentText("");
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setPosting(false);
    }
  };

  const handleCommentKeyDown = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      handleCommentSubmit();
    } else if (e.key === "Escape") {
      setCommentText("");
    }
  };

  return (
    <div className="flex flex-col h-full bg-background/50">
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LuLoaderCircle className="w-4 h-4 animate-spin text-secondary-foreground/50" />
          </div>
        ) : comments.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <span className="text-xs text-secondary-foreground/40">
              No comments yet
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {comments.map((comment) => (
              <CommentGroup
                key={comment._id}
                data={comment}
                onDelete={fetchComments}
                onUpdate={updateComment}
              />
            ))}
          </div>
        )}
      </div>

      <UserRole role="commentator">
        <div className="p-2 border-t">
          <div className="flex flex-col gap-1">
            <CommentMentionsInput
              value={commentText}
              onChange={setCommentText}
              onKeyDown={handleCommentKeyDown}
              placeholder="Add a comment..."
              inputRef={commentInputRef}
            />
            <div className="flex items-center justify-end gap-1">
              <Button
                type="confirm"
                size="xs"
                onClick={handleCommentSubmit}
                disabled={!commentText.trim() || posting}>
                Comment
              </Button>
            </div>
          </div>
        </div>
      </UserRole>
    </div>
  );
}

// Renders the actions section below the tabs. Each action is rendered only when
// its callback is provided so callers can opt-in to specific actions. The modal
// holds no logic itself — every button just invokes the parent callback with the
// asset.
function ActionsSection({
  asset,
  onDelete,
  onCopy,
  onMove,
  onSketchEdit,
  onScene3DEdit,
  onCaptureFrame,
  capturing,
  onTrim,
  trimming,
  trimProgress,
  onDownload,
  onCopyUrl,
  onRestoreFromArchive,
  onDestroy,
  onAddToList,
}) {
  const actions = [];
  const isArchived = Boolean(asset?.archived);
  const canAddToAssetsList = isAssetsListMediaTypeAllowed(asset?.mediaType);

  if (isArchived === false && onCaptureFrame && asset.mediaType === "video") {
    actions.push(
      <Button
        key="capture"
        id="AssetPreview__actionCapture"
        type="secondary"
        disabled={capturing}
        icon={
          capturing ? (
            <LuLoaderCircle className="w-4 h-4 animate-spin" />
          ) : (
            <FiCamera className="w-4 h-4" />
          )
        }
        onClick={() => onCaptureFrame(asset)}>
        {capturing ? "Capturing…" : "Capture Frame"}
      </Button>,
    );
  }

  if (
    isArchived === false &&
    onTrim &&
    asset.mediaType === "video" &&
    isTrimSupported()
  ) {
    actions.push(
      <Button
        key="trim"
        id="AssetPreview__actionTrim"
        type="secondary"
        disabled={trimming}
        icon={
          trimming ? (
            <LuLoaderCircle className="w-4 h-4 animate-spin" />
          ) : (
            <FiScissors className="w-4 h-4" />
          )
        }
        onClick={() => onTrim(asset)}>
        {trimming
          ? trimProgress > 0
            ? `Trimming… ${Math.round(trimProgress * 100)}%`
            : "Trimming…"
          : "Trim Video"}
      </Button>,
    );
  }

  if (isArchived === false && onSketchEdit && asset.mediaType === "image") {
    actions.push(
      <Button
        key="edit"
        id="AssetPreview__actionEdit"
        type="secondary"
        icon={<FiImage className="w-4 h-4" />}
        onClick={() => onSketchEdit(asset)}>
        Edit
      </Button>,
    );
  }

  if (isArchived === false && onScene3DEdit && asset.scene3d) {
    actions.push(
      <Button
        key="edit-scene3d"
        type="secondary"
        icon={<FiBox className="w-4 h-4" />}
        onClick={() => onScene3DEdit(asset)}>
        Edit 3D Scene
      </Button>,
    );
  }

  if (asset?.url) {
    actions.push(
      <Button
        key="download"
        id="AssetPreview__actionDownload"
        type="secondary"
        icon={<FiDownload className="w-4 h-4" />}
        onClick={() => onDownload(asset)}>
        Download
      </Button>,
    );
  }

  if (asset?.url && onCopyUrl) {
    actions.push(
      <Button
        key="copy-url"
        id="AssetPreview__actionCopyUrl"
        type="secondary"
        icon={<FiLink className="w-4 h-4" />}
        onClick={() => onCopyUrl(asset)}>
        Copy URL
      </Button>,
    );
  }

  // Audio can't be added to an assets list, so the button never offers it.
  if (onAddToList && canAddToAssetsList) {
    actions.push(
      <Button
        key="add-to-list"
        id="AssetPreview__actionAddToList"
        type="secondary"
        icon={<FiList className="w-4 h-4" />}
        onClick={onAddToList}>
        Add to Asset List
      </Button>,
    );
  }

  if (isArchived === false && onCopy) {
    actions.push(
      <Button
        key="copy"
        id="AssetPreview__actionCopy"
        type="secondary"
        icon={<FiCopy className="w-4 h-4" />}
        onClick={() => onCopy(asset)}>
        Duplicate
      </Button>,
    );
  }

  if (isArchived === false && onMove) {
    actions.push(
      <Button
        key="move"
        id="AssetPreview__actionMove"
        type="secondary"
        icon={<MdDriveFileMove className="w-4 h-4" />}
        onClick={() => onMove(asset)}>
        Move
      </Button>,
    );
  }

  if (isArchived === false && onDelete) {
    actions.push(
      <Button
        key="archive"
        id="AssetPreview__actionArchive"
        type="danger"
        icon={<FiTrash className="w-4 h-4" />}
        onClick={() => onDelete(asset)}>
        Archive
      </Button>,
    );
  }

  if (isArchived && onRestoreFromArchive) {
    actions.push(
      <Button
        key="restore"
        id="AssetPreview__actionRestore"
        type="secondary"
        icon={<MdRestoreFromTrash className="w-4 h-4" />}
        onClick={() => onRestoreFromArchive([asset._id])}>
        Restore
      </Button>,
    );
  }

  if (isArchived && onDestroy) {
    actions.push(
      <Button
        key="destroy"
        id="AssetPreview__actionDestroy"
        type="danger"
        icon={<MdDeleteForever className="w-4 h-4" />}
        onClick={() => onDestroy(asset)}>
        Delete
      </Button>,
    );
  }

  if (actions.length === 0) return null;

  return (
    <div className="border-t p-3">
      <div className="grid grid-cols-2 gap-2">{actions}</div>
    </div>
  );
}

export default function AssetPreview({
  asset,
  onDelete,
  onCopy,
  onMove,
  onSketchEdit,
  onScene3DEdit,
  onCaptured,
  onRenamed,
  onRefreshAsset,
  onClose,
  onPrev,
  onNext,
  positionLabel,
  initialTab = "info",
  onRestoreFromArchive,
  onDestroy,
  onAddToList,
}) {
  const [tab, setTab] = useState(initialTab);
  const [capturing, setCapturing] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [trimProgress, setTrimProgress] = useState(0);
  const [trimStatus, setTrimStatus] = useState("");
  // UI-only lifecycle flags for the inline rename spinner/error. The name
  // itself always comes from the `asset` prop — see handleRenameSave.
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState(null);
  const videoPlayerRef = useRef(null);
  const { show } = useShow();
  const { openModal } = useModal();
  const { uploadFile } = useFileUpload();
  const { downloadFile } = useFileDownload();

  const handleDownload = useCallback(
    async (target) => {
      if (!target?.url) return;
      try {
        await downloadFile(target.url, target.name);
      } catch (error) {
        console.error("Error downloading file:", error);
        toast.error("Failed to download asset");
      }
    },
    [downloadFile],
  );

  const handleCopyUrl = useCallback(async (target) => {
    try {
      await navigator.clipboard.writeText(target.url);
      toast("Asset URL copied to clipboard");
    } catch {
      toast.error("Failed to copy asset URL");
    }
  }, []);

  // Images have no trim handles, so their in/out are fixed rather than read
  // from a player; an untouched video means "whole clip", not 0/duration.
  const getTrimTimes = useCallback(() => {
    if (asset.mediaType === "image") return { inTime: 0, outTime: 5 };

    const range = videoPlayerRef.current?.getTrimRange();
    if (range?.hasSelection) return { inTime: range.start, outTime: range.end };
    return { inTime: null, outTime: null };
  }, [asset.mediaType]);

  // Frame rate and frame size are only knowable from the player that decoded
  // the file, and the edit decision list export needs both. Capture them at
  // the moment the asset joins a list; an image has none to give.
  const getSourceMedia = useCallback(() => {
    const emptySourceMedia = {
      frameRate: null,
      duration: null,
      width: null,
      height: null,
    };

    if (asset.mediaType === "image") return emptySourceMedia;

    return videoPlayerRef.current?.getMediaInfo() ?? emptySourceMedia;
  }, [asset.mediaType]);

  const handleAddToList = useCallback(() => {
    return onAddToList?.({
      asset,
      ...getTrimTimes(),
      sourceMedia: getSourceMedia(),
    });
  }, [onAddToList, asset, getTrimTimes, getSourceMedia]);

  // Clear any stale rename error when the previewed asset changes.
  useEffect(() => {
    setNameError(null);
  }, [asset?._id]);

  // Persist an inline name edit. Mirrors AssetItem's rename: PUT the new name,
  // then let the parent re-fetch so the FE re-renders from the authoritative
  // server data (we keep the spinner up across the refetch so the old name
  // never flashes back). No local copy of the name is kept — it always comes
  // from the `asset` prop. Spinner/error surface via savingName/nameError.
  const handleRenameSave = useCallback(
    async (name) => {
      if (!show?._id || !asset?._id) return;
      setSavingName(true);
      setNameError(null);
      try {
        const res = await apiFetch(
          `/api/shows/${show._id}/assets/${asset._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
          },
        );
        if (!res.ok) throw new Error("Failed to rename asset");
        await onRenamed?.(asset._id);
      } catch (error) {
        console.error("Error renaming asset:", error);
        setNameError("Failed to rename asset");
      } finally {
        setSavingName(false);
      }
    },
    [show?._id, asset?._id, onRenamed],
  );

  // Capture the current video frame, upload it, and create a new image asset
  // in the same folder as the source video.
  const handleCaptureFrame = useCallback(async () => {
    if (!show?._id || capturing) return;
    const player = videoPlayerRef.current;
    if (!player?.captureFrame) return;
    try {
      setCapturing(true);
      const blob = await player.captureFrame("image/png");
      const baseName = asset.name || "Asset";
      const file = new File([blob], `${baseName}-frame.png`, {
        type: "image/png",
      });
      const uploadResponse = await uploadFile(file);
      const res = await apiFetch(`/api/shows/${show._id}/assets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...uploadResponse,
          mediaType: "image",
          name: `Frame capture from ${baseName}`,
          source: "capture",
          folder: asset.folder || null,
        }),
      });
      if (!res.ok) throw new Error("Failed to create asset");
      const created = await res.json();
      toast.success("Frame captured");
      onCaptured?.(created);
    } catch (error) {
      console.error("Error capturing frame:", error);
      toast.error("Failed to capture frame");
    } finally {
      setCapturing(false);
    }
  }, [show?._id, asset, uploadFile, capturing, onCaptured]);

  // Trim the current video to its in/out selection, upload the result, and
  // create a new video asset. The user first picks a destination folder and a
  // name via the folder picker (with naming enabled).
  const handleTrim = useCallback(async () => {
    if (!show?._id || trimming) return;
    const player = videoPlayerRef.current;
    if (!player?.trim || !player?.getTrimRange) return;

    const range = player.getTrimRange();
    if (!range || !(range.end > range.start)) {
      toast.error("Set a valid trim range first");
      return;
    }

    const baseName = asset.name || "Asset";
    const pick = await openModal(
      <FolderPickerModal
        title="Trim Video"
        confirmLabel="Trim & Save"
        allowNaming
        nameLabel="New asset name"
        namePlaceholder="Trimmed clip name"
        initialName={`${baseName} (trimmed)`}
      />,
    );
    if (!pick) return;

    try {
      setTrimming(true);
      setTrimProgress(0);
      setTrimStatus("Processing video…");
      const blob = await player.trim(range.start, range.end, {
        onProgress: setTrimProgress,
      });
      setTrimStatus("Uploading…");
      const file = new File([blob], `${pick.assetName}.mp4`, {
        type: "video/mp4",
      });
      const uploadResponse = await uploadFile(file);
      setTrimStatus("Creating asset…");
      const res = await apiFetch(`/api/shows/${show._id}/assets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...uploadResponse,
          mediaType: "video",
          name: pick.assetName,
          source: "trim",
          folder: pick.folderId ?? null,
        }),
      });
      if (!res.ok) throw new Error("Failed to create asset");
      const created = await res.json();
      toast.success("Trimmed video created");
      onCaptured?.(created);
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Error trimming video:", error);
      toast.error("Failed to trim video");
    } finally {
      setTrimming(false);
      setTrimProgress(0);
      setTrimStatus("");
    }
  }, [show?._id, asset, uploadFile, trimming, openModal, onCaptured]);

  if (!asset) return null;

  return (
    <div
      // Literal, not `AssetPreview.name` — minifiers rename functions, so the
      // built bundle emitted an id like "e" and every #AssetPreview selector
      // silently stopped matching. Dev looked perfect, which is what made it
      // invisible in review.
      id="AssetPreview"
      // Archiving advances the preview in place, so "the preview is open" says
      // nothing about whether it advanced to the right asset — only identity does.
      data-asset-id={asset?._id}
      className="relative flex w-full h-full overflow-hidden">
      {/* Full-cover progress overlay shown while a trim is in flight. */}
      {trimming && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 w-[320px] max-w-[80%] px-6">
            <div className="relative flex items-center justify-center">
              <LuLoaderCircle className="w-12 h-12 text-primary animate-spin" />
              <FiScissors className="w-5 h-5 text-primary absolute" />
            </div>
            <div className="flex flex-col items-center gap-1 w-full">
              <span className="text-sm font-medium text-foreground">
                {trimStatus || "Trimming video…"}
              </span>
              <span className="text-xs text-secondary-foreground/60">
                {Math.round(trimProgress * 100)}%
              </span>
            </div>
            <Progress
              value={Math.round(trimProgress * 100)}
              className="w-full"
            />
            <span className="text-[11px] text-secondary-foreground/50 text-center">
              Trimming runs in your browser — keep this tab open.
            </span>
          </div>
        </div>
      )}
      <div className="relative flex-1 min-w-0 bg-black flex items-center justify-center">
        <AssetMedia
          key={asset._id}
          asset={asset}
          videoPlayerRef={videoPlayerRef}
        />

        {onPrev && (
          <button
            id="AssetPreview__prev"
            type="button"
            title="Previous"
            onClick={onPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 backdrop-blur text-white hover:bg-black/70 cursor-pointer">
            <FiChevronLeft className="w-5 h-5" />
          </button>
        )}

        {onNext && (
          <button
            id="AssetPreview__next"
            type="button"
            title="Next"
            onClick={onNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 backdrop-blur text-white hover:bg-black/70 cursor-pointer">
            <FiChevronRight className="w-5 h-5" />
          </button>
        )}

        {positionLabel && (
          <div
            id="AssetPreview__position"
            className="absolute top-3 left-1/2 -translate-x-1/2 z-10 rounded-md bg-black/60 backdrop-blur px-2 py-1 text-white text-xs tabular-nums pointer-events-none">
            {positionLabel}
          </div>
        )}
      </div>
      <div className="w-[380px] shrink-0 flex flex-col border-l bg-background">
        <div className="flex items-start gap-2 px-4 pt-4 pb-2">
          <div className="min-w-0 flex-1">
            <AsyncEditableTextArea
              id="AssetPreview__name"
              value={asset.name}
              onSave={handleRenameSave}
              loading={savingName}
              error={nameError}
              placeholder="Asset"
              title="Double-click to rename"
              variant="heading"
            />
            {asset.mediaType && (
              <span className="text-xs text-secondary-foreground/60 capitalize">
                {asset.mediaType}
              </span>
            )}
          </div>
          {onClose && (
            <button
              id="AssetPreview__close"
              type="button"
              onClick={() => onClose(null)}
              className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-md text-secondary-foreground/70 hover:text-foreground hover:bg-secondary/60 cursor-pointer"
              title="Close">
              <FiX className="w-5 h-5" />
            </button>
          )}
        </div>
        <Tabs
          value={tab}
          onValueChange={setTab}
          className="flex-1 min-h-0 flex flex-col gap-0">
          <TabsList className="w-full rounded-none">
            <TabsTrigger value="info">Info</TabsTrigger>
            <TabsTrigger value="comments">Comments</TabsTrigger>
          </TabsList>
          <TabsContent value="info" className="flex-1 min-h-0 overflow-hidden">
            <InfoTab asset={asset} onRefreshAsset={onRefreshAsset} />
          </TabsContent>
          <TabsContent
            value="comments"
            className="flex-1 min-h-0 overflow-hidden">
            <AssetComments assetId={asset._id} />
          </TabsContent>
        </Tabs>
        <ActionsSection
          asset={asset}
          onDelete={onDelete}
          onCopy={onCopy}
          onMove={onMove}
          onSketchEdit={onSketchEdit}
          onScene3DEdit={onScene3DEdit}
          onCaptureFrame={handleCaptureFrame}
          capturing={capturing}
          onTrim={handleTrim}
          trimming={trimming}
          trimProgress={trimProgress}
          onDownload={handleDownload}
          onCopyUrl={handleCopyUrl}
          onRestoreFromArchive={onRestoreFromArchive}
          onDestroy={onDestroy}
          onAddToList={onAddToList ? handleAddToList : undefined}
        />
      </div>
    </div>
  );
}
